import React, { ReactElement, useCallback, useMemo } from "react";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import { useTable, useExpanded, useSortBy, Column, TableState } from "react-table";
import { Filter } from "../models";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
} from "@material-ui/core";
import {
  ArrowDownward,
  ArrowUpward,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@material-ui/icons";

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    maxHeight: "100%",
    maxWidth: "100% ",
    width: "auto",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100% ",
    },
  },
  table: { width: "auto", maxWidth: "100%", maxHeight: "100%" },
  cell: {
    cursor: "pointer",
    padding: `${theme.goldenRatio(-3)}rem ${theme.goldenRatio(2)}rem ${theme.goldenRatio(
      -3
    )}rem ${theme.goldenRatio(-3)}rem`,
    "& small": {
      fontSize: "inherit",
      fontWeight: "normal",
      color: theme.palette.grey.A200,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: `${theme.goldenRatio(-0.5)}rem`,
      padding: `${theme.goldenRatio(-3)}rem`,
    },
  },
  expanderCell: {
    padding: `0 !important`,
    width: `${theme.goldenRatio(3)}rem`,
  },
  headerCell: {
    padding: `${theme.goldenRatio(-3)}rem ${theme.goldenRatio(2)}rem ${theme.goldenRatio(
      -3
    )}rem ${theme.goldenRatio(-3)}rem`,
    background: "white",
    textTransform: "uppercase",
    fontWeight: 900,
    minWidth: "max-content",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      fontSize: `${theme.goldenRatio(-0.5)}rem`,
      padding: `${theme.goldenRatio(-3)}rem`,
    },
  },
  chartWrapper: {
    width: "fit-content",
    height: "fit-content",
    display: "flex",
    flexFlow: "column nowrap",
    placeContent: "center",
    placeItems: "center",
    placeSelf: "center",
    minWidth: "10rem",
    minHeight: "10rem",
    maxWidth: "16rem",
    maxHeight: "16rem",
  },
  pieChart: {
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
    placeSelf: "center",
    height: "10rem",
    overflow: "visible !important",
    padding: "0 3rem",
    width: "unset",
  },
  expanderButton: {
    height: "1rem",
    width: "1rem",
  },
}));

export default function FooterTable({
  data,
  columns,
  expander = false,
  hoverFilter,
  isFilterMatch,
  setHoverFilter,
  initialState,
}: {
  data: any;
  columns: Column[];
  expander?: boolean;
  hoverFilter?: Filter[];
  isFilterMatch?: (args: any) => boolean | undefined;
  setHoverFilter?: (filters: Filter[]) => void;
  initialState?: Partial<TableState<{}>>;
}): ReactElement {
  const classes = useStyles();
  const theme = useTheme();

  const isFilterActive = useMemo(() => Boolean(hoverFilter?.length), [hoverFilter]);

  const handleHover = useCallback(
    (e: React.MouseEvent, filters?: Filter[]) => {
      e.preventDefault();
      e.stopPropagation();
      if (setHoverFilter) filters ? setHoverFilter(filters) : setHoverFilter([]);
    },
    [setHoverFilter]
  );

  const expanderColumn: Column = useMemo(
    () => ({
      id: "expander",
      Header: function expanderHeader({
        getToggleAllRowsExpandedProps,
        isAllRowsExpanded,
      }: {
        getToggleAllRowsExpandedProps: any;
        isAllRowsExpanded: any;
      }) {
        return (
          <IconButton
            classes={{ root: classes.expanderButton }}
            {...getToggleAllRowsExpandedProps()}
          >
            {isAllRowsExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
        );
      },
      Cell: function expanderCell({ column, row }: any) {
        return row.canExpand ? (
          <IconButton
            {...row.getToggleRowExpandedProps({
              style: {
                marginLeft: `${row.depth * 0.618}rem`,
              },
            })}
            classes={{ root: classes.expanderButton }}
          >
            {row.isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
        ) : null;
      },
    }),
    [classes.expanderButton]
  );

  columns = useMemo(() => (expander ? [expanderColumn, ...columns] : columns), [
    columns,
    expanderColumn,
    expander,
  ]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      autoResetExpanded: false,
      autoResetSortBy: false,
      initialState,
    },
    useSortBy,
    ...(expander ? [useExpanded] : [])
  );

  return (
    <TableContainer className={classes.wrapper}>
      <Table
        {...getTableProps}
        size="small"
        stickyHeader={true}
        className={classes.table}
      >
        <TableHead>
          {headerGroups.map((headerGroup: any) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                <TableCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={[
                    classes.headerCell,
                    ...(column.id === "expander" ? [classes.expanderCell] : []),
                  ].join(" ")}
                >
                  {column.render("Header")}
                  {column.isSorted ? (
                    <IconButton size="small">
                      {column.isSortedDesc ? (
                        <ArrowDownward style={{ fontSize: "0.8rem" }} color="secondary" />
                      ) : (
                        <ArrowUpward style={{ fontSize: "0.8rem" }} color="secondary" />
                      )}
                    </IconButton>
                  ) : (
                    ""
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps}>
          {rows.map((row: any) => {
            const isMatch = isFilterMatch && isFilterMatch(row.original?.filters);
            prepareRow(row);
            return (
              <TableRow
                hover
                {...row.getRowProps()}
                // {...(expander ? row.getToggleRowExpandedProps() : {})}
                onMouseEnter={(e: any) => handleHover(e, row.original?.filters)}
                onMouseLeave={(e) => handleHover(e)}
              >
                {row.cells.map((cell: any) => (
                  <TableCell
                    {...cell.getCellProps({
                      style: {
                        ...(row.depth === 0 ? { fontWeight: 500 } : {}),
                        ...(isFilterActive
                          ? isMatch
                            ? {
                                backgroundColor: theme.palette.grey[200],
                              }
                            : {
                                color: theme.palette.grey[500],
                              }
                          : {}),
                      },
                    })}
                    className={[
                      classes.cell,
                      ...(cell.column.id === "expander" ? [classes.expanderCell] : []),
                    ].join(" ")}
                  >
                    {cell.render("Cell")}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
